import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

import {
  listenToAuthChanges,
  loginWithEmailPassword,
  logout as firebaseLogout,
} from "../services/authService";
import {
  getUserAccessByAuthUser,
  normalizeUserEmail,
} from "../services/userAccessService";

const AuthContext = createContext({
  user: null,
  role: null,
  isAdmin: false,
  isOwner: false,
  isLoading: true,
  accessMessage: "",
  accessDetail: "",
  loginWithEmailPassword: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }) {
  const [authState, setAuthState] = useState({
    user: null,
    role: null,
    isLoading: true,
    accessMessage: "",
    accessDetail: "",
  });
  const deniedStateRef = useRef(null);
  const authRequestRef = useRef(0);

  useEffect(() => {
    let isMounted = true;

    const unsubscribe = listenToAuthChanges(async (nextUser) => {
      const requestId = authRequestRef.current + 1;
      authRequestRef.current = requestId;

      if (!isMounted) {
        return;
      }

      if (!nextUser) {
        const deniedState = deniedStateRef.current;
        deniedStateRef.current = null;

        setAuthState({
          user: null,
          role: null,
          isLoading: false,
          accessMessage: deniedState?.accessMessage ?? "",
          accessDetail: deniedState?.accessDetail ?? "",
        });
        return;
      }

      setAuthState((currentState) => ({
        ...currentState,
        isLoading: true,
        accessMessage: "",
        accessDetail: "",
      }));

      const email = normalizeUserEmail(nextUser.email);

      if (!email) {
        deniedStateRef.current = {
          accessMessage: "Access denied",
          accessDetail: "You are not authorized to access this system",
        };
        await firebaseLogout();
        return;
      }

      try {
        const accessRecord = await getUserAccessByAuthUser(nextUser);

        if (!isMounted || requestId !== authRequestRef.current) {
          return;
        }

        if (!accessRecord) {
          deniedStateRef.current = {
            accessMessage: "Access denied",
            accessDetail: "You are not authorized to access this system",
          };
          await firebaseLogout();
          return;
        }

        if (!accessRecord.isActive) {
          deniedStateRef.current = {
            accessMessage: "Account disabled",
            accessDetail: "Your account has been disabled",
          };
          await firebaseLogout();
          return;
        }

        setAuthState({
          user: nextUser,
          role: accessRecord.role,
          isLoading: false,
          accessMessage: "",
          accessDetail: "",
        });
      } catch (error) {
        console.error("AUTH ACCESS CHECK ERROR:", error);
        deniedStateRef.current = {
          accessMessage: "Access denied",
          accessDetail: "Unable to verify access permissions",
        };
        await firebaseLogout();
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const logout = async () => {
    deniedStateRef.current = null;
    await firebaseLogout();
  };

  const value = useMemo(() => ({
    user: authState.user,
    role: authState.role,
    isAdmin: ["owner", "admin"].includes(authState.role),
    isOwner: authState.role === "owner",
    isLoading: authState.isLoading,
    accessMessage: authState.accessMessage,
    accessDetail: authState.accessDetail,
    loginWithEmailPassword,
    logout,
  }), [authState]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
